"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  INPUT_MONO,
  LABEL,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { parseLines } from "@/lib/tool-kit/classroom/entries";
import {
  allocateMinutes,
  LESSON_TEMPLATES,
  type LessonPlan,
  type LessonPlanLabels,
  type LessonTemplate,
  lessonPlanHtml,
  lessonPlanText,
} from "@/lib/tool-kit/classroom/lesson-plan";
import { downloadText } from "@/lib/tool-kit/download";

const MAX_MINUTES = 600;
const NOT_FILE_SAFE = /[^\p{L}\p{N}]+/gu;

/** English strings. A translated content file overrides these through `ui`. */
export const LESSON_PLAN_GENERATOR_STRINGS = {
  titleLabel: "Lesson title",
  titlePlaceholder: "Food Chains and Energy Flow",
  subjectLabel: "Subject",
  subjectPlaceholder: "Science",
  gradeLabel: "Grade or class",
  gradePlaceholder: "Grade 5",
  durationLabel: "Lesson length (minutes)",
  templateLabel: "Template",
  templates: {
    simple: "Simple",
    "five-e": "5E model",
    hunter: "Madeline Hunter",
  },
  templateHints: {
    simple:
      "Introduction, instruction, practice, closure — a plain sequence for any lesson.",
    "five-e":
      "Engage, Explore, Explain, Elaborate, Evaluate — the BSCS inquiry model, common in science.",
    hunter:
      "Madeline Hunter's lesson-design elements, from the anticipatory set to independent practice.",
  },
  objectivesLabel: "Learning objectives — one per line",
  objectivesPlaceholder:
    "Students will be able to trace the flow of energy through a food chain.",
  standardsLabel: "Standards — one per line (optional)",
  standardsPlaceholder: "NGSS 5-LS2-1",
  materialsLabel: "Materials — one per line",
  materialsPlaceholder: "Organism cards\nYarn\nExit ticket",
  procedure: "Procedure",
  minutesLabel: "Minutes",
  notesLabel: "What happens",
  stageNames: {
    introduction: "Introduction",
    instruction: "Instruction",
    practice: "Practice",
    closure: "Closure",
    engage: "Engage",
    explore: "Explore",
    explain: "Explain",
    elaborate: "Elaborate",
    evaluate: "Evaluate",
    "anticipatory-set": "Anticipatory set",
    objective: "Objective and purpose",
    input: "Input",
    modeling: "Modeling",
    check: "Checking for understanding",
    "guided-practice": "Guided practice",
    "independent-practice": "Independent practice",
  },
  stagePrompts: {
    introduction:
      "Hook interest and connect the lesson to what students already know.",
    instruction:
      "Teach the new idea or skill: explain, demonstrate and give examples.",
    practice: "Students apply it, first with support and then on their own.",
    closure:
      "Pull the lesson together: students sum up what they learned and you preview what comes next.",
    engage:
      "Spark curiosity and surface prior knowledge with a question, demonstration or puzzling event.",
    explore:
      "Students investigate hands-on before any formal explanation, gathering their own observations.",
    explain:
      "Students explain what they found; you introduce the vocabulary and clarify the concept.",
    elaborate:
      "Students apply the concept to a new situation to extend and deepen it.",
    evaluate:
      "Students and teacher check understanding against the objectives.",
    "anticipatory-set":
      "A short opener that focuses attention and links to earlier learning.",
    objective: "Tell students what they will learn today and why it matters.",
    input: "Present the new information or skill in clear, logical steps.",
    modeling:
      "Show what the skill or finished product looks like, thinking aloud.",
    check: "Ask questions or run quick checks before moving on.",
    "guided-practice":
      "Students practice while you circulate, giving immediate feedback.",
    "independent-practice":
      "Students practice alone once they can work without major errors — in class or as homework.",
  },
  assessmentLabel: "Assessment",
  assessmentPlaceholder:
    "Exit ticket: draw a four-link food chain and label the energy flow.",
  differentiationLabel: "Differentiation",
  differentiationPlaceholder:
    "Word bank and sentence frames for the explain stage; extension: a food web with two predators.",
  reflectionLabel: "Reflection (after teaching)",
  reflectionPlaceholder: "What worked, what to change next time.",
  minutes: "one:{count} minute|other:{count} minutes",
  totalOk: "The stages add up to {total}, the full lesson.",
  totalOff: "The stages add up to {total}, but the lesson is {duration}.",
  fit: "Fit timings to the lesson length",
  badDuration: "Enter a lesson length between 1 and {max} minutes.",
  loadExample: "Load example",
  print: "Print",
  copy: "Copy plan",
  copied: "Copied!",
  download: "Download .txt",
  preview: "Preview",
  untitled: "Lesson plan",
  subject: "Subject",
  grade: "Grade",
  duration: "Duration",
  objectives: "Objectives",
  standards: "Standards",
  materials: "Materials",
  stage: "Stage",
  time: "Time",
  activities: "Activities",
  total: "Total",
  assessment: "Assessment",
  differentiation: "Differentiation",
  reflection: "Reflection",
  reflectionBlank: "To complete after teaching.",
  example: {
    title: "Food Chains and Energy Flow",
    subject: "Science",
    grade: "Grade 5",
    objectives:
      "Students will be able to trace the flow of energy through a four-link food chain.\nStudents will be able to classify organisms as producers, consumers or decomposers.",
    standards: "NGSS 5-LS2-1",
    materials:
      "Organism picture cards\nBall of yarn\nFood chain worksheet\nExit tickets",
    assessment:
      "Exit ticket: draw a four-link food chain, label each organism's role and show the direction of energy flow.",
    differentiation:
      "Sentence frames and a word bank during Explain; extension groups build a food web with two top predators.",
    engage:
      "Show a photo of an owl eating a mouse. Ask: where did the owl's energy come from before that?",
    explore:
      "Groups use organism cards and yarn to connect who eats whom, then record their chains.",
    explain:
      "Groups share chains; introduce producer, consumer, decomposer and the arrow convention.",
    elaborate:
      "Remove one organism from a chain and predict the effects on the others.",
    evaluate: "Exit ticket, then review two answers together.",
  },
} as const;

type Strings = typeof LESSON_PLAN_GENERATOR_STRINGS;
type StageId = keyof Strings["stageNames"];
type Field =
  | "title"
  | "subject"
  | "grade"
  | "objectives"
  | "standards"
  | "materials"
  | "assessment"
  | "differentiation"
  | "reflection";

const EMPTY: Record<Field, string> = {
  title: "",
  subject: "",
  grade: "",
  objectives: "",
  standards: "",
  materials: "",
  assessment: "",
  differentiation: "",
  reflection: "",
};

function labelsFrom(
  strings: Strings,
  locale: string,
  fmt: (value: number) => string
): LessonPlanLabels {
  return {
    untitled: strings.untitled,
    subject: strings.subject,
    grade: strings.grade,
    duration: strings.duration,
    objectives: strings.objectives,
    standards: strings.standards,
    materials: strings.materials,
    procedure: strings.procedure,
    stage: strings.stage,
    time: strings.time,
    activities: strings.activities,
    total: strings.total,
    assessment: strings.assessment,
    differentiation: strings.differentiation,
    reflection: strings.reflection,
    minutes: (count) =>
      tPlural(strings.minutes, locale, count, { count: fmt(count) }),
  };
}

function Preview({
  plan,
  labels,
  strings,
}: {
  plan: LessonPlan;
  labels: LessonPlanLabels;
  strings: Strings;
}) {
  const list = (heading: string, items: string[]) =>
    items.length > 0 ? (
      <div className="space-y-1">
        <h3 className="font-semibold text-base">{heading}</h3>
        <ul className="list-disc space-y-1 ps-5 text-sm">
          {items.map((item, index) => (
            <li key={`${heading}-${index + 1}-${item}`}>{item}</li>
          ))}
        </ul>
      </div>
    ) : null;
  const block = (heading: string, body: string) =>
    body.trim() ? (
      <div className="space-y-1">
        <h3 className="font-semibold text-base">{heading}</h3>
        <p className="whitespace-pre-line text-sm">{body.trim()}</p>
      </div>
    ) : null;
  const header = [
    plan.subject.trim() && `${labels.subject}: ${plan.subject.trim()}`,
    plan.grade.trim() && `${labels.grade}: ${plan.grade.trim()}`,
    `${labels.duration}: ${labels.minutes(plan.duration)}`,
  ].filter(Boolean);
  return (
    <section className="space-y-4 rounded-lg border border-border p-5 text-foreground print:border-0 print:p-0 print:text-black">
      <h2 className="font-semibold text-xl">
        {plan.title.trim() || labels.untitled}
      </h2>
      <p className="text-sm">{header.join(" · ")}</p>
      {list(labels.objectives, plan.objectives)}
      {list(labels.standards, plan.standards)}
      {list(labels.materials, plan.materials)}
      <div className="space-y-2">
        <h3 className="font-semibold text-base">{labels.procedure}</h3>
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr>
              <th className="border border-current px-2 py-1" scope="col">
                {labels.stage}
              </th>
              <th className="border border-current px-2 py-1" scope="col">
                {labels.time}
              </th>
              <th className="border border-current px-2 py-1" scope="col">
                {labels.activities}
              </th>
            </tr>
          </thead>
          <tbody>
            {plan.stages.map((stage) => (
              <tr className="break-inside-avoid align-top" key={stage.name}>
                <td className="border border-current px-2 py-1 font-medium">
                  {stage.name}
                </td>
                <td className="whitespace-nowrap border border-current px-2 py-1">
                  {labels.minutes(stage.minutes)}
                </td>
                <td className="whitespace-pre-line border border-current px-2 py-1">
                  {stage.notes}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {block(labels.assessment, plan.assessment)}
      {block(labels.differentiation, plan.differentiation)}
      {block(labels.reflection, plan.reflection) ?? (
        <div className="hidden space-y-1 print:block">
          <h3 className="font-semibold text-base">{labels.reflection}</h3>
          <p className="text-sm italic">{strings.reflectionBlank}</p>
          {["a", "b", "c", "d"].map((line) => (
            <div className="h-8 border-gray-400 border-b" key={line} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function LessonPlanGeneratorWidget() {
  const strings = useToolUi(LESSON_PLAN_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [fields, setFields] = useState<Record<Field, string>>(EMPTY);
  const [template, setTemplate] = useState<LessonTemplate>("five-e");
  const [durationInput, setDurationInput] = useState("45");
  const [minuteInputs, setMinuteInputs] = useState<string[] | null>(null);
  const [notes, setNotes] = useState<Partial<Record<StageId, string>>>({});

  const stages = LESSON_TEMPLATES[template];
  const duration = parseIntegerInput(durationInput);
  const durationOk =
    duration !== null && duration > 0 && duration <= MAX_MINUTES;
  const minutes =
    minuteInputs?.map((value) => Math.max(0, parseIntegerInput(value) ?? 0)) ??
    allocateMinutes(
      durationOk ? duration : 0,
      stages.map((stage) => stage.weight)
    );
  const total = minutes.reduce((sum, value) => sum + value, 0);
  const labels = labelsFrom(strings, locale, fmt);
  const plan: LessonPlan = {
    title: fields.title,
    subject: fields.subject,
    grade: fields.grade,
    duration: durationOk ? duration : total,
    objectives: parseLines(fields.objectives),
    standards: parseLines(fields.standards),
    materials: parseLines(fields.materials),
    stages: stages.map((stage, index) => ({
      name: strings.stageNames[stage.id as StageId],
      minutes: minutes[index] ?? 0,
      notes: notes[stage.id as StageId] ?? "",
    })),
    assessment: fields.assessment,
    differentiation: fields.differentiation,
    reflection: fields.reflection,
  };
  const text = lessonPlanText(plan, labels);

  const set = (key: Field) => (value: string) =>
    setFields((previous) => ({ ...previous, [key]: value }));

  function loadExample() {
    const { example } = strings;
    setFields({ ...EMPTY, ...example, reflection: "" });
    setTemplate("five-e");
    setDurationInput("45");
    setMinuteInputs(null);
    setNotes({
      engage: example.engage,
      explore: example.explore,
      explain: example.explain,
      elaborate: example.elaborate,
      evaluate: example.evaluate,
    });
  }

  function setStageMinutes(index: number, value: string) {
    const current = minuteInputs ?? minutes.map(String);
    setMinuteInputs(current.map((item, i) => (i === index ? value : item)));
  }

  const field = (
    key: Field,
    label: string,
    placeholder: string,
    area = false
  ) =>
    area ? (
      <TextAreaField
        id={`lsp-${key}`}
        label={label}
        onChange={set(key)}
        placeholder={placeholder}
        prose
        rows={3}
        value={fields[key]}
      />
    ) : (
      <TextField
        id={`lsp-${key}`}
        label={label}
        onChange={set(key)}
        placeholder={placeholder}
        value={fields[key]}
      />
    );

  return (
    <div className="space-y-6">
      <div className="space-y-4 print:hidden">
        <button className={BUTTON_SMALL} onClick={loadExample} type="button">
          {strings.loadExample}
        </button>
        {field("title", strings.titleLabel, strings.titlePlaceholder)}
        <div className="grid gap-4 sm:grid-cols-3">
          {field("subject", strings.subjectLabel, strings.subjectPlaceholder)}
          {field("grade", strings.gradeLabel, strings.gradePlaceholder)}
          <TextField
            id="lsp-duration"
            label={strings.durationLabel}
            numeric
            onChange={setDurationInput}
            value={durationInput}
          />
        </div>
        {durationOk ? null : (
          <ErrorNotice>
            {t(strings.badDuration, { max: MAX_MINUTES })}
          </ErrorNotice>
        )}
        {field(
          "objectives",
          strings.objectivesLabel,
          strings.objectivesPlaceholder,
          true
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          {field(
            "standards",
            strings.standardsLabel,
            strings.standardsPlaceholder,
            true
          )}
          {field(
            "materials",
            strings.materialsLabel,
            strings.materialsPlaceholder,
            true
          )}
        </div>
        <PillGroup
          label={strings.templateLabel}
          name="lsp-template"
          onChange={(next) => {
            setTemplate(next);
            setMinuteInputs(null);
          }}
          options={(["simple", "five-e", "hunter"] as const).map((value) => ({
            value,
            label: strings.templates[value],
          }))}
          value={template}
        />
        <p className={HINT}>{strings.templateHints[template]}</p>
        <fieldset className="space-y-3">
          <legend className={LABEL}>{strings.procedure}</legend>
          {stages.map((stage, index) => {
            const id = stage.id as StageId;
            return (
              <div
                className="grid gap-2 rounded-lg border border-border p-3 sm:grid-cols-[1fr_6rem]"
                key={id}
              >
                <div className="space-y-1">
                  <label className={LABEL} htmlFor={`lsp-notes-${id}`}>
                    {strings.stageNames[id]}
                  </label>
                  <textarea
                    className="min-h-[72px] w-full resize-y rounded-lg border border-border bg-card p-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    id={`lsp-notes-${id}`}
                    onChange={(event) =>
                      setNotes((previous) => ({
                        ...previous,
                        [id]: event.target.value,
                      }))
                    }
                    placeholder={strings.stagePrompts[id]}
                    value={notes[id] ?? ""}
                  />
                </div>
                <div className="space-y-1">
                  <label className={LABEL} htmlFor={`lsp-minutes-${id}`}>
                    {strings.minutesLabel}
                  </label>
                  <input
                    className={INPUT_MONO}
                    id={`lsp-minutes-${id}`}
                    inputMode="numeric"
                    onChange={(event) =>
                      setStageMinutes(index, event.target.value)
                    }
                    value={minuteInputs?.[index] ?? String(minutes[index] ?? 0)}
                  />
                </div>
              </div>
            );
          })}
        </fieldset>
        <div aria-live="polite" className="space-y-2">
          {durationOk && total !== duration ? (
            <Notice>
              {t(strings.totalOff, {
                total: labels.minutes(total),
                duration: labels.minutes(duration),
              })}{" "}
              <button
                className={BUTTON_SMALL}
                onClick={() =>
                  setMinuteInputs(
                    allocateMinutes(
                      duration,
                      total > 0 ? minutes : stages.map((s) => s.weight)
                    ).map(String)
                  )
                }
                type="button"
              >
                {strings.fit}
              </button>
            </Notice>
          ) : null}
          {durationOk && total === duration ? (
            <p className={HINT}>
              {t(strings.totalOk, { total: labels.minutes(total) })}
            </p>
          ) : null}
        </div>
        {field(
          "assessment",
          strings.assessmentLabel,
          strings.assessmentPlaceholder,
          true
        )}
        {field(
          "differentiation",
          strings.differentiationLabel,
          strings.differentiationPlaceholder,
          true
        )}
        {field(
          "reflection",
          strings.reflectionLabel,
          strings.reflectionPlaceholder,
          true
        )}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <CopyButton
            copiedLabel={strings.copied}
            html={lessonPlanHtml(plan, labels)}
            label={strings.copy}
            text={text}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              downloadText(
                `${(fields.title.trim() || strings.untitled).replace(NOT_FILE_SAFE, "-")}.txt`,
                text
              )
            }
            type="button"
          >
            {strings.download}
          </button>
        </div>
        <h2 className="font-semibold text-foreground text-xl">
          {strings.preview}
        </h2>
      </div>
      <Preview labels={labels} plan={plan} strings={strings} />
    </div>
  );
}
